import styled from '@emotion/styled';
import { graphql } from 'gatsby';
import { useMemo, type FC } from 'react';
import { FaFacebook, FaInstagram, FaSquareXTwitter } from 'react-icons/fa6';

import { ScreenReaderText } from '@/components/common';
import { Anchor } from '@/components/common/atoms';
import { media } from '@/styles';

type Props = Queries.SnsMenuListItemFragment & {
  className?: string;
};

export const fragment = graphql`
  fragment SnsMenuListItem on WpMenuItem {
    label
    url
    cssClasses
  }
`;

const useIcon = ({ cssClasses }: Pick<Props, 'cssClasses'>) =>
  useMemo(() => {
    if (!cssClasses) {
      return null;
    }
    const cssClassesString = cssClasses.join(' ');

    if (/facebook/.exec(cssClassesString)) {
      return <FaFacebook />;
    } else if (/twitter/.exec(cssClassesString)) {
      return <FaSquareXTwitter />;
    } else if (/instagram/.exec(cssClassesString)) {
      return <FaInstagram />;
    }
    return null;
  }, [cssClasses]);

export const Item: FC<Props> = ({ className, label, url, cssClasses }) => {
  const icon = useIcon({ cssClasses });
  if (!icon) {
    return null;
  }
  if (!url) {
    return null;
  }
  return (
    <li className={className ?? ''}>
      <StyledAnchor className={cssClasses?.join(' ')} to={url}>
        {icon}
        <ScreenReaderText>{label}</ScreenReaderText>
      </StyledAnchor>
    </li>
  );
};

const StyledAnchor = styled(Anchor)`
  &:hover,
  &:focus-visible {
    opacity: 0.5;
  }
  &.twitter-link {
    font-size: 1.2em;
  }
  &.facebook-link {
    font-size: 1.1em;
  }
  &.instagram-link {
    font-size: 1.25em;
  }
  ${media.ipadVerticalOrMore} {
    &.twitter-link {
      font-size: 1.3em;
    }
    &.facebook-link {
      font-size: 1.2em;
    }
    &.instagram-link {
      font-size: 1.35em;
    }
  }
  ${media.siteBreakpointOrMore} {
    &.twitter-link {
      font-size: 1.1em;
    }
    &.facebook-link {
      font-size: 1.05em;
    }
    &.instagram-link {
      font-size: 1.15em;
    }
  }
`;

export default Item;
